import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import UserService, {
  userServiceKeys,
} from 'api/services/httpServices/UserService';
import { ResendCodeViaEmail } from './ResendCodeViaEmail';
import { Button } from 'modules/shared-components/button/button';
import Input from 'modules/shared-components/input/Input';
import { FullPageModal } from 'modules/shared-components/modal/FullPage';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import * as yup from 'yup';
import { duration } from 'moment';
import { Link } from 'react-router-dom';
import CustomModal from 'modules/shared-components/modal/CustomModal';
import { useUserState } from 'modules/user/UserContext';
import { NotificationDex } from '../../../app/dex/components/alerts/notification';

const schema = yup.object({
  code: yup
    .string()
    .required('Required')
    .length(6, 'Code should be 6 chars long'),
});

export type OtpFormValues = yup.InferType<typeof schema>;

export interface Props {
  showAuthentication: boolean;
  onSubmit: (values: OtpFormValues) => void;
  handleClose: () => void;
  errors?: JSX.Element;
  isSubmitLoading?: boolean;
}

const TwoFactorAuthentication = (props: PropsWithChildren<Props>) => {
  const { showAuthentication, handleClose, children } = props;

  const { data: otpConfig } = useQuery({
    queryFn: () => UserService.getOtpConfig(),
    queryKey: userServiceKeys.getOtpConfig(),
    staleTime: duration('15', 'minutes').asMilliseconds(),
  });

  if (!showAuthentication) return <>{children}</>;

  if (!otpConfig?.enabled) {
    return (
      <>
        <CustomModal showModal handleClose={handleClose}>
          <CustomModal.Body className="xxs:p-4 text-left">
            <CustomModal.Title className="xxs:text-3xl xxs:leading-7 font-semibold">
              Two factor authentication is not enabled
            </CustomModal.Title>
            <NotificationDex type="warning" className="text-start">
              Enabling two factor authentication is mandatory for payouts.
              Please navigate to{' '}
              <Link to="/dashboard/settings/Account">Account settings</Link> and
              enable two factor authentication first.
            </NotificationDex>
          </CustomModal.Body>
          <CustomModal.Footer>
            <Button onClick={handleClose} variant="primary">
              Close
            </Button>
          </CustomModal.Footer>
        </CustomModal>
      </>
    );
  }
  return <TwoFactorModal {...props} mechanism={otpConfig.mechanism} />;
};

type TwoFactorModalProps = {
  onSubmit: (values: OtpFormValues) => void;
  handleClose: () => void;
  errors?: JSX.Element;
  isSubmitLoading?: boolean;
  mechanism: 'email' | 'app';
};

export function TwoFactorModal({
  onSubmit,
  handleClose,
  errors,
  isSubmitLoading,
  mechanism,
}: TwoFactorModalProps) {
  const refSubmitButtom = useRef<HTMLButtonElement>(null);
  const methods = useForm<OtpFormValues>({
    resolver: yupResolver(schema),
  });

  const code = methods.watch('code');
  useEffect(() => {
    if (code?.length === 6) {
      refSubmitButtom?.current?.click();
    }
  }, [code]);

  return (
    <FormProvider {...methods}>
      <FullPageModal
        showModal
        handleClose={handleClose}
        className="max-w-[450px] "
        backdropClassName="sm:items-center"
      >
        <form
          className="contents"
          onSubmit={(e) => {
            void methods.handleSubmit(onSubmit)(e);
          }}
        >
          <FullPageModal.Body className="xxs:p-4 text-left ">
            <FullPageModal.Title className="xxs:text-3xl font-semibold xxs:leading-7">
              Two Factor Authentication
            </FullPageModal.Title>
            {mechanism === 'email' && <EmailTwoFactorAuthentication />}
            {mechanism === 'app' && <AppTwoFactorAuthentication />}
            <div className="xxs:my-2">{errors}</div>
          </FullPageModal.Body>
          <FullPageModal.Footer>
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitLoading}
              ref={refSubmitButtom}
            >
              Confirm
            </Button>
            <Button onClick={handleClose} type="button" variant="outline">
              Cancel
            </Button>
          </FullPageModal.Footer>
        </form>
      </FullPageModal>
    </FormProvider>
  );
}

export default TwoFactorAuthentication;

function EmailTwoFactorAuthentication() {
  const { user } = useUserState();
  const {
    register,
    formState: { errors },
  } = useFormContext<OtpFormValues>();

  return (
    <FullPageModal.Content>
      <div className="xxs:text-base dark:text-white-200 xxs:mb-4">
        Enter the 6 digits code received at {user?.email || 'your email'}
      </div>
      <NotificationDex type="warning" className="xxs:my-2 xxs:mb-4">
        A new authentication code will be sent after every attempt, do not use
        the same token twice
      </NotificationDex>

      <Input
        {...register('code')}
        type="text"
        name="code"
        label="Authentication Code"
        id="code"
        data-testid="code"
        errorText={errors.code?.message}
        className="xxs:mb-2"
      />
      {user && 'access_token' in user ? (
        <ResendCodeViaEmail />
      ) : (
        <span className="xxs:text-xs dark:text-white-200">
          If you did not receive an email, please check in your spam or contact
          info@mizar.ai for support.
        </span>
      )}
    </FullPageModal.Content>
  );
}

function AppTwoFactorAuthentication() {
  const {
    register,
    formState: { errors },
  } = useFormContext<OtpFormValues>();

  return (
    <FullPageModal.Content>
      <div className="xxs:text-lg dark:text-white xxs:mb-4">
        Enter the 6 digits code from your authenticator app.
      </div>

      <Input
        {...register('code')}
        type="text"
        name="code"
        label="Authentication code"
        id="code"
        data-testid="code"
        errorText={errors.code?.message}
      />
    </FullPageModal.Content>
  );
}
